// Shared display formatters. Single source of truth for currency and
// clock-time presentation — previously duplicated inline across pages,
// which let locale/format drift between screens showing the same value.

/** Cents → "€ 12,50" (nl-NL currency formatting). */
export const formatEuros = (cents: number): string =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(cents / 100)

/**
 * An ISO 8601 API timestamp → Date, or null when it cannot be read.
 *
 * Every field this is applied to is ISO on the API since 2026-10-08: the Order timestamps
 * (`created_at` / `updated_at` joined `scheduled_for` and `current_status_since`), the route
 * and loyalty-program moments. Not every type is — Payment, DiscountRule, DiscountCode and
 * others still serialise epoch milliseconds (API todo, audit 2026-10-08) — so the screens
 * reading THOSE keep their own tolerant parser and must not switch to this one until the
 * API converts the type. Null, never "Invalid Date", when the value cannot be read.
 */
export const parseApiDate = (value?: string | null): Date | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** API timestamp → epoch ms for sorting; 0 when unreadable, so a comparator never sees NaN. */
export const apiTime = (value?: string | null): number => parseApiDate(value)?.getTime() ?? 0

/** API timestamp → "18:30" (nl-NL, 2-digit hour/minute, device timezone); "—" if unreadable. */
export const formatTime = (iso: string): string =>
  parseApiDate(iso)?.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) ?? '—'

/** Seconds → "45s" / "3m 20s" / "3m". */
export const formatDuration = (seconds: number): string => {
  // Round the whole figure first: the stats averages are SQL floats, and
  // rounding the remainder alone turns 899.6 s into "14m 60s".
  const total = Math.round(seconds)
  if (total <= 0) return '0s'
  if (total < 60) return `${total}s`
  const m = Math.floor(total / 60)
  const s = total % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

/**
 * "YYYY-MM-DD" → a LOCAL Date. Never `new Date("YYYY-MM-DD")` — that reads as UTC
 * midnight and shifts the day in CET/CEST. Lives here (not in pages/reports/shared) so
 * lib modules can use it without importing a page module; shared re-exports it.
 */
export const parseYmd = (date: string): Date => {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** LOCAL Date → "YYYY-MM-DD". The inverse of parseYmd; never toISOString(). */
export const toYmd = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

// "YYYY-MM-DD" range → local-day UTC ISO bounds (0:00:00 / 23:59:59 local), the
// convention orderStats expects for date_from/date_to. Never parse via
// new Date("YYYY-MM-DD") — that reads as UTC midnight and shifts the day in CET/CEST.
export const dateRangeToUtc = (start: string, end: string) => {
  const [sy, sm, sd] = start.split('-').map(Number)
  const [ey, em, ed] = end.split('-').map(Number)
  return {
    date_from: new Date(sy, sm - 1, sd, 0, 0, 0).toISOString(),
    date_to: new Date(ey, em - 1, ed, 23, 59, 59).toISOString(),
  }
}

/**
 * The current LOCAL calendar day as an ISO-UTC window, for API filters that
 * compare against UTC-stored timestamps (e.g. scheduled_from/scheduled_to).
 * Call per render — do not memoize at mount: always-on POS terminals cross
 * midnight while mounted, and a frozen window keeps querying yesterday.
 */
export const localDayBoundsUtc = (): { scheduledFrom: string; scheduledTo: string } => {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const d = now.getDate()
  return {
    scheduledFrom: new Date(y, m, d, 0, 0, 0).toISOString(),
    scheduledTo: new Date(y, m, d, 23, 59, 59).toISOString(),
  }
}

/**
 * A Date → the value a `<input type="datetime-local">` holds, in the terminal's
 * local time ("2026-09-20T18:00"). Read back with `new Date(value)`, which
 * interprets the string as local time again, then `.toISOString()` for the API.
 */
export const localDateTimeString = (date: Date): string =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)

/**
 * The phone number with its Uber Eats relay access code when the order has one:
 * "+31858882358 · code 014 19 685". Uber Eats hands us a relay number, and whoever dials
 * it must key in the code to reach the customer, so the code travels with the number
 * wherever it is shown or copied. The tel: link itself stays on the bare number — a DTMF
 * suffix in the href is unreliable across phones. Empty when there is no phone.
 */
export const phoneWithAccessCode = (order: {
  customer_phone?: string | null
  customer_phone_access_code?: string | null
}): string => {
  if (!order.customer_phone) return ''
  return order.customer_phone_access_code
    ? `${order.customer_phone} · code ${order.customer_phone_access_code}`
    : order.customer_phone
}
